import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from "react";
import { IconPaperclip, IconX } from "@tabler/icons-react";
import { Button as VpButton, Loader as VpLoader } from "@/shared/ui";
import { analyzeStyleReference, getStyleReferenceAvailability, } from "@/shared/api/styleReference";
import { STYLE_REFERENCE_ACCEPT, isSupportedStyleReferenceFile, styleReferenceErrorMessage as errorMessage, } from "@/shared/lib/styleReference";
import styles from "./StyleReferenceField.module.scss";
// Фолбэк лимита, пока не пришёл ответ /availability (совпадает с бэком).
const DEFAULT_MAX_SIZE_MB = 50;
/**
 * «Прикрепить референс (аудио или видео)»: файл уходит на бэк, ИИ описывает
 * его стиль, песня генерируется «в стиле» референса (мелодия не копируется).
 * Прячется, если на бэке фича не сконфигурирована (`/availability`).
 */
export const StyleReferenceField = ({ language, value, onChange, onBusyChange, }) => {
    const ru = language === "ru";
    const inputRef = useRef(null);
    const mountedRef = useRef(true);
    const [available, setAvailable] = useState(null);
    const [maxSizeMb, setMaxSizeMb] = useState(DEFAULT_MAX_SIZE_MB);
    const [segmentSeconds, setSegmentSeconds] = useState(60);
    const [phase, setPhase] = useState("idle");
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState(null);
    useEffect(() => {
        mountedRef.current = true;
        let cancelled = false;
        getStyleReferenceAvailability()
            .then((res) => {
            if (cancelled)
                return;
            setAvailable(res.available);
            if (res.max_size_mb)
                setMaxSizeMb(res.max_size_mb);
            if (res.segment_seconds)
                setSegmentSeconds(res.segment_seconds);
        })
            .catch(() => {
            if (!cancelled)
                setAvailable(false);
        });
        return () => {
            cancelled = true;
            mountedRef.current = false;
        };
    }, []);
    const busy = phase !== "idle";
    const reportedBusyRef = useRef(false);
    // Сообщаем только СМЕНУ busy: при повторном монтировании (вернулись на таб
    // «Мелодия», а анализ, начатый прошлым экземпляром, ещё идёт) начальный
    // `false` не должен разблокировать «Создать песню».
    useEffect(() => {
        if (reportedBusyRef.current === busy)
            return;
        reportedBusyRef.current = busy;
        onBusyChange?.(busy);
    }, [busy, onBusyChange]);
    const handleFile = async (file) => {
        if (!file)
            return;
        setError(null);
        if (!isSupportedStyleReferenceFile(file)) {
            setError(errorMessage({ response: { status: 400 } }, language, maxSizeMb));
            return;
        }
        if (file.size > maxSizeMb * 1024 * 1024) {
            setError(errorMessage({ response: { status: 413 } }, language, maxSizeMb));
            return;
        }
        setPhase("uploading");
        setProgress(0);
        try {
            const result = await analyzeStyleReference(file, (fraction) => {
                if (!mountedRef.current)
                    return;
                setProgress(fraction);
                if (fraction >= 1)
                    setPhase("analyzing");
            });
            // Результат отдаём родителю даже после размонтирования поля: таб
            // «Мелодия» рендерится условно, и уход на «Текст» посреди анализа иначе
            // выбрасывал готовый (и уже посчитанный в лимитах) референс.
            onChange({
                id: result.reference_id,
                description: result.style_description,
                fileName: file.name,
            });
        }
        catch (e) {
            if (!mountedRef.current)
                return;
            setError(errorMessage(e, language, maxSizeMb));
            // Фича выключена на бэке — больше не предлагаем загрузку.
            if (e?.response?.status === 503) {
                setAvailable(false);
            }
        }
        finally {
            // Поле размонтировано (ушли на таб «Текст») — эффект по `busy` уже не
            // сработает: снимаем блокировку «Создать песню» сами, но только когда
            // анализ реально закончился (иначе песня ушла бы без референса).
            if (mountedRef.current)
                setPhase("idle");
            else
                onBusyChange?.(false);
        }
    };
    // Восстановленный из черновика референс показываем, даже если /availability
    // ещё не ответил или ответил «нет» — описание уже лежит на бэке.
    if (!value && !available && !error)
        return null;
    return (_jsxs("div", { className: styles.field, children: [_jsxs("div", { className: styles.label, children: [ru ? "Референс стиля" : "Style reference", _jsx("span", { className: styles.labelSuffix, children: ru ? " (необязательно)" : " (optional)" })] }), value ? (_jsxs("div", { className: styles.chip, children: [_jsxs("div", { className: styles.chipText, children: [_jsx("span", { className: styles.chipTitle, children: ru ? "Стиль референса: " : "Reference style: " }), value.description, _jsx("div", { className: styles.chipFile, children: value.fileName })] }), _jsx("button", { type: "button", className: styles.chipRemove, onClick: () => {
                            onChange(null);
                            setError(null);
                        }, "aria-label": ru ? "Убрать референс" : "Remove reference", children: _jsx(IconX, { size: 16 }) })] })) : busy ? (_jsxs("div", { className: styles.status, children: [_jsx(VpLoader, { size: "sm" }), _jsx("span", { children: phase === "uploading" && progress < 1
                            ? ru
                                ? `Загружаем файл… ${Math.round(progress * 100)}%`
                                : `Uploading… ${Math.round(progress * 100)}%`
                            : ru
                                ? "Слушаем трек и разбираем стиль…"
                                : "Listening and analyzing the style…" })] })) : (available && (_jsx(VpButton, { variant: "outline", size: "md", leftSection: _jsx(IconPaperclip, { size: 16 }), onClick: () => inputRef.current?.click(), children: ru
                    ? "Прикрепить референс (аудио или видео)"
                    : "Attach a reference (audio or video)" }))), !value && !busy && available && (_jsx("div", { className: styles.hint, children: ru
                    ? `Сделаем песню в стиле трека: жанр, темп, инструменты, вокал. Мелодию не копируем. До ${maxSizeMb} МБ, анализируем ${segmentSeconds} секунд.`
                    : `We'll match the track's style: genre, tempo, instruments, vocals. The melody isn't copied. Up to ${maxSizeMb} MB, ${segmentSeconds} seconds are analyzed.` })), error && _jsx("div", { className: styles.error, children: error }), _jsx("input", { ref: inputRef, type: "file", accept: STYLE_REFERENCE_ACCEPT, hidden: true, onChange: (e) => {
                    const file = e.target.files?.[0];
                    // Сбрасываем, чтобы повторный выбор того же файла снова сработал.
                    e.target.value = "";
                    void handleFile(file);
                } })] }));
};
